import type { QuotaQueueInfo } from '../utils/quotaQueue';

import axiosInstance from './axiosInstance';
import { tokenService } from './tokenService';

const SHARE_INFO_CACHE_MS = 30000;

interface ShareInfoCache {
  authToken: string | null;
  data: ShareInfo;
  fetchedAt: number;
}

interface ShareInfoRequest {
  authToken: string | null;
  promise: Promise<ShareInfo>;
}

let shareInfoCache: ShareInfoCache | null = null;
let shareInfoRequest: ShareInfoRequest | null = null;

export type SharePlatform = 'linkedin' | 'instagram';

export interface ShareItem {
  id: string;
  project_id: string | null;
  project_name: string | null;
  platform: string;
  status: string;
  progress: number;
  progress_stage: string | null;
  video_url: string | null;
  caption: string | null;
  highlight_prompt?: string | null;
  reel_url: string | null;
  posted_at: string | null;
  share_credits_awarded: number;
  views_reported: number;
  view_credits_awarded: number;
  views_checked_at: string | null;
  claimable_at: string | null;
  claimable: boolean;
  views_claimed: boolean;
  link_deleted: boolean;
  created_at: string | null;
  /** Set while the reel waits for its plan's hourly quota. */
  quota_queue?: QuotaQueueInfo | null;
}

export interface ShareInfo {
  eligible: boolean;
  next_share_at: string | null;
  extension_install_url: string;
  shares: ShareItem[];
  total_share_credits: number;
  total_shares_posted: number;
}

export interface ClaimContext {
  share_id: string;
  platform: string;
  reel_url: string | null;
  posted_at: string | null;
  claimable: boolean;
  already_claimed: boolean;
  video_url: string | null;
  extension_install_url?: string | null;
}

export interface ClaimResult extends ShareItem {
  credits_awarded: number;
}

export interface AutomationSession {
  automation_token: string;
  expires_in_seconds: number;
}

/** Parse a backend timestamp (naive UTC, no zone suffix) into epoch milliseconds. */
export const parseUtcMs = (iso: string): number => {
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);

  return new Date(hasZone ? iso : `${iso}Z`).getTime();
};

/** Human countdown until a backend timestamp, e.g. "in 3h 12m". */
export const formatCountdown = (targetIso: string | null, nowMs: number): string => {
  if (!targetIso) return 'soon';

  const remainingMs = parseUtcMs(targetIso) - nowMs;

  if (remainingMs <= 0) return 'now';

  const totalMinutes = Math.ceil(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return hours > 0 ? `in ${hours}h ${minutes}m` : `in ${minutes}m`;
};

/** Extract a FastAPI error detail string from an axios error, with a fallback message. */
export const extractApiError = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;

  return typeof detail === 'string' ? detail : fallback;
};

const cachedShareInfoFor = (authToken: string | null): ShareInfoCache | null => (
  shareInfoCache?.authToken === authToken ? shareInfoCache : null
);

const fetchShareInfo = (authToken: string | null): Promise<ShareInfo> => {
  const request = axiosInstance
    .get<ShareInfo>('/api/v1/promotion/share-info')
    .then((response) => {
      if (tokenService.getAccessToken() === authToken) {
        shareInfoCache = { authToken, data: response.data, fetchedAt: Date.now() };
      }

      return response.data;
    })
    .finally(() => {
      if (shareInfoRequest?.promise === request) shareInfoRequest = null;
    });

  shareInfoRequest = { authToken, promise: request };

  return request;
};

export const promotionService = {
  getCachedShareInfo(): ShareInfo | null {
    return cachedShareInfoFor(tokenService.getAccessToken())?.data ?? null;
  },

  async getShareInfo(force = false): Promise<ShareInfo> {
    const authToken = tokenService.getAccessToken();
    const cached = cachedShareInfoFor(authToken);
    const cacheFresh = cached && Date.now() - cached.fetchedAt < SHARE_INFO_CACHE_MS;

    if (!force && cacheFresh) return cached.data;
    if (shareInfoRequest?.authToken === authToken) return shareInfoRequest.promise;

    return fetchShareInfo(authToken);
  },

  prefetchShareInfo(): void {
    void this.getShareInfo().catch(() => undefined);
  },

  async createShare(
    projectId: string,
    highlightPrompt: string,
    platform?: SharePlatform,
  ): Promise<ShareItem> {
    const response = await axiosInstance.post<ShareItem>('/api/v1/promotion/shares', {
      project_id: projectId,
      highlight_prompt: highlightPrompt,
      ...(platform ? { platform } : {}),
    });

    return response.data;
  },

  async resetShareCooldown(): Promise<ShareInfo> {
    const response = await axiosInstance.post<ShareInfo>('/api/v1/promotion/share-cooldown/reset');

    return response.data;
  },

  async getShare(shareId: string): Promise<ShareItem> {
    const response = await axiosInstance.get<ShareItem>(`/api/v1/promotion/shares/${shareId}`);

    return response.data;
  },

  async cancelShare(shareId: string): Promise<ShareItem> {
    const response = await axiosInstance.post<ShareItem>(`/api/v1/promotion/shares/${shareId}/cancel`);

    return response.data;
  },

  /** Try again on a reel held for the plan's hourly quota (429 with the new wait while it is still spent). */
  async retryShare(shareId: string): Promise<ShareItem> {
    const response = await axiosInstance.post<ShareItem>(`/api/v1/promotion/shares/${shareId}/retry`);

    return response.data;
  },

  async regenerateShare(shareId: string, highlightPrompt: string): Promise<ShareItem> {
    const response = await axiosInstance.post<ShareItem>(`/api/v1/promotion/shares/${shareId}/regenerate`, {
      highlight_prompt: highlightPrompt,
    });

    return response.data;
  },

  async createAutomationSession(shareId: string): Promise<AutomationSession> {
    const response = await axiosInstance.post<AutomationSession>(
      `/api/v1/promotion/shares/${shareId}/automation-session`,
    );

    return response.data;
  },

  async markPosted(shareId: string, reelUrl: string, platform?: SharePlatform): Promise<ShareItem> {
    const response = await axiosInstance.post<ShareItem>(`/api/v1/promotion/shares/${shareId}/posted`, {
      reel_url: reelUrl,
      ...(platform ? { platform } : {}),
    });

    return response.data;
  },

  async getClaimContext(token: string): Promise<ClaimContext> {
    const response = await axiosInstance.get<ClaimContext>(`/api/v1/promotion/shares/claim/${token}`);

    return response.data;
  },

  async claimViews(token: string, views: number, linkAlive: boolean): Promise<ClaimResult> {
    const response = await axiosInstance.post<ClaimResult>('/api/v1/promotion/shares/claim', {
      claim_token: token,
      views,
      link_alive: linkAlive,
    });

    return response.data;
  },

  async claimShareViews(shareId: string, views: number, linkAlive: boolean): Promise<ClaimResult> {
    const response = await axiosInstance.post<ClaimResult>(`/api/v1/promotion/shares/${shareId}/claim`, {
      views,
      link_alive: linkAlive,
    });

    return response.data;
  },

  async unlockShareViewClaim(shareId: string): Promise<ShareItem> {
    const response = await axiosInstance.post<ShareItem>(`/api/v1/promotion/shares/${shareId}/claim/unlock`);

    return response.data;
  },
};
